import { SettingsIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Icon,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { useAskPatBeta, useSettings } from '../../context';

const SettingsPage: React.FC = () => {
  const { isLoading } = useSettings();
  const { isAskPatEnabled, toggleAskPat } = useAskPatBeta();
  //const { colorBlind, toggleColorBlind } = useTheme();
  const toast = useToast();

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Text>Loading settings...</Text>
      </Box>
    );
  }

  const handleAskPatToggle = () => {
    const newAskPatState = !isAskPatEnabled;
    toggleAskPat();

    toast({
      title: `Ask PAT ${newAskPatState ? 'enabled' : 'disabled'}`,
      description: newAskPatState
        ? 'Ask PAT feature is now enabled. You can ask questions about markets and practices.'
        : 'Ask PAT feature has been disabled.',
      status: 'success',
      duration: 4000,
      isClosable: true,
    });
  };

  // Theme change handler (commented out for future use)
  // const handleThemeChange = (newTheme: 'light' | 'dark') => {
  //   setTheme(newTheme);
  //   toast({
  //     title: 'Theme updated',
  //     description: `Switched to ${newTheme} theme.`,
  //     status: 'success',
  //     duration: 2000,
  //     isClosable: true,
  //   });
  // };

  // const handleColorBlindToggle = () => {
  //   toggleColorBlind();
  //   toast({
  //     title: `High contrast mode ${
  //       !colorBlind ? 'enabled' : 'disabled'
  //     }`,
  //     description: !colorBlind
  //       ? 'Enhanced contrast for better accessibility.'
  //       : 'Standard contrast restored.',
  //     status: 'success',
  //     duration: 2000,
  //     isClosable: true,
  //   });
  // };

  return (
    <Box maxW="4xl" mx="auto" p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack spacing={3}>
          <Icon as={SettingsIcon} boxSize={6} color="blue.500" />
          <Heading size="lg">Settings</Heading>
        </HStack>

        {/* Beta Features Section */}
        <Card>
          <CardHeader>
            <Heading size="md">Experimental Features</Heading>
            <Text fontSize="sm" color="gray.600" mt={1}>
              Enable experimental features that are still in
              development
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between" align="center">
                <Box>
                  <Text fontWeight="medium">Ask PAT</Text>
                  <Text fontSize="sm" color="gray.600">
                    Enable AI assistant to answer questions about markets
                    and practices
                  </Text>
                </Box>
                <Switch
                  isChecked={isAskPatEnabled}
                  onChange={handleAskPatToggle}
                  colorScheme="blue"
                  size="lg"
                  aria-label="Enable Ask PAT feature"
                />
              </HStack>

              {isAskPatEnabled && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="sm">Feature Active</AlertTitle>
                    <AlertDescription fontSize="sm">
                      Ask PAT is now enabled. You'll see the "Ask PAT"
                      button on market and practice detail pages to chat
                      with the AI assistant.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Appearance Section */}
        <Card>
          <CardHeader>
            <Heading size="md">Appearance</Heading>
            <Text fontSize="sm" color="gray.600" mt={1}>
              Customize the visual appearance of the application
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              {/* Theme Selection - Commented out for now as requested */}
              {/*
              <Box>
                <Text fontWeight="medium" mb={2}>Theme</Text>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant={theme === 'light' ? 'solid' : 'outline'}
                    colorScheme={theme === 'light' ? 'blue' : 'gray'}
                    onClick={() => handleThemeChange('light')}
                  >
                    Light
                  </Button>
                  <Button
                    size="sm"
                    variant={theme === 'dark' ? 'solid' : 'outline'}
                    colorScheme={theme === 'dark' ? 'blue' : 'gray'}
                    onClick={() => handleThemeChange('dark')}
                  >
                    Dark
                  </Button>
                </HStack>
              </Box>
              */}

              {/* Color Blind Support */}
              {/* <HStack justify="space-between" align="center">
                <Box>
                  <Text fontWeight="medium">High Contrast Mode</Text>
                  <Text fontSize="sm" color="gray.600">
                    Enhanced contrast for better accessibility and
                    color-blind support
                  </Text>
                </Box>
                <Switch
                  isChecked={colorBlind}
                  onChange={handleColorBlindToggle}
                  colorScheme="blue"
                  size="lg"
                  aria-label="Enable high contrast mode"
                />
              </HStack> */}

              {/* Future theme options placeholder */}
              <Box p={3} bg="gray.50" borderRadius="md">
                <Text
                  fontSize="sm"
                  color="gray.500"
                  fontStyle="italic"
                >
                  Additional theme options for dark mode, color blind
                  mode coming soon...
                </Text>
              </Box>
            </VStack>
          </CardBody>
        </Card>

        {/* Accessibility Section */}
        {/* <Card>
          <CardHeader>
            <Heading size="md">Accessibility</Heading>
            <Text fontSize="sm" color="gray.600" mt={1}>
              Configure accessibility options for a better user
              experience
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Box p={3} bg="gray.50" borderRadius="md">
                <Text
                  fontSize="sm"
                  color="gray.500"
                  fontStyle="italic"
                >
                  Additional accessibility options coming soon...
                </Text>
              </Box>
            </VStack>
          </CardBody>
        </Card> */}

        {/* Data & Privacy Section */}
        <Card>
          <CardHeader>
            <Heading size="md">Data & Privacy</Heading>
            <Text fontSize="sm" color="gray.600" mt={1}>
              Information about data collection and privacy
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={3} align="stretch">
              <Text fontSize="sm" color="gray.700">
                <strong>What we store:</strong> Your app preferences
                (theme and enabled experimental features) are saved locally in your
                browser to enhance your experience.
              </Text>

              <Text fontSize="sm" color="gray.700">
                <strong>Privacy:</strong> All data is processed
                according to our privacy policy. You can disable
                experimental features at any time.
              </Text>

              <HStack spacing={4} pt={2}>
                <Button
                  size="sm"
                  variant="outline"
                  as="a"
                  href="/privacy-policy"
                >
                  Privacy Policy
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  as="a"
                  href="/cookie-policy"
                >
                  Data Storage Policy
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default SettingsPage;
